import { CopyIcon, TrashIcon } from "@radix-ui/react-icons";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import IconTip from "components/Dashboard/PeoplePanel/IconTip";
import {
  Dialog,
  DialogAction,
  DialogActions,
  DialogClose,
  DialogContent,
} from "components/Dialog";
import { errorText } from "components/Forms/formsApi";
import { useEffect, useRef, useState } from "react";
import Notifications from "util/notifications";
import AskFirstControl from "./AskFirstControl";
import EditorDialog from "./AutomationsTabDialog";
import {
  firstStep,
  followTrigger,
  nameAfter,
  orderSteps,
  switchAction,
  waitersOf,
  withNext,
} from "./AutomationsTabModel";
import { ActionIcon, plainTitle } from "./AutomationsTabTimeline";
import { NextWhenField, WhenField } from "./AutomationsTabTiming";
import {
  GateCardFields,
  tasksBefore,
  triggerFor,
} from "./AutomationsTabTrigger";
import { useAutomationsApi, useReference } from "./hooks";
import { actionsFor, defaultTrigger, isTaskKind, kindTiming } from "./kinds";
import {
  cardTime,
  clone,
  fitToTrigger,
  gateProblems,
  newCard,
  stepProblems,
} from "./model";
import { ChoiceSelect, FieldRow } from "./parts";
import type { ReferenceData } from "./reference";
import StepActionFields from "./StepActionFields";
import StepMessagePreview from "./StepMessagePreview";
import TriggerPicker, { TriggerKindGrid } from "./TriggerPicker";
import TurnOnDialog from "./TurnOnDialog";
import useStepEstimate from "./useStepEstimate";
import type {
  AutomationDraft,
  Gate,
  Position,
  Step,
  StepAction,
  TimePosition,
  Trigger,
} from "./types";
import { ACTION_LABELS, cardTimeWords, suggestName } from "./words";

/** What the step dialog opens on. */
export interface EditorTarget {
  id: string;
  /**
   * new: a new automation (draft null until its trigger is picked); add: a
   * new step; edit: a saved step; gate: a new trigger gate (gateId, no card
   * until its kind is picked) and the step right after it.
   */
  mode: "new" | "add" | "edit" | "gate";
  automationId: string | null;
  draft: AutomationDraft | null;
  stepId: string | null;
  /** The new gate (gate). */
  gateId?: string | null;
  /** Add next step: When, at the dialog's top, sets the new step's timing. */
  when: boolean;
  /** What opened it (a button, or a step's card), which gets the focus back. */
  anchor: HTMLElement;
  title: string;
}

const ACTIONS = Object.keys(ACTION_LABELS) as StepAction[];
const WORDS_INPUT = 'input[aria-label="Words in the task name"]';

/** A new automation on this kind, with its first step ready to save. */
function newDraft(ref: ReferenceData, kind: string): AutomationDraft {
  const trigger = defaultTrigger(ref, kind);
  return { name: "", on: true, trigger, steps: [firstStep(ref, trigger)] };
}

/** Then: what the step does, as the app's dropdown, each action with its icon. */
function ActionSelect({
  value,
  onChange,
  trigger,
}: {
  value: StepAction;
  onChange: (action: StepAction) => void;
  trigger: Trigger;
}) {
  const ref = useReference();
  const allowed = actionsFor(ref, trigger);
  return (
    <ChoiceSelect<StepAction>
      label="Then"
      value={value}
      options={ACTIONS.map((action) => ({
        value: action,
        label: ACTION_LABELS[action],
        icon: <ActionIcon action={action} />,
        isDisabled: !allowed.includes(action),
      }))}
      searchable={false}
      onChange={(action) => action !== value && onChange(action)}
      className="w-full"
    />
  );
}

/**
 * The step dialog: for a new automation its trigger first, for Add next
 * step its When, for Add a trigger gate the gate's kinds, then its card
 * (and that card's own timing); then what the step does (Then), the
 * action's fields, Ask first, a message's preview or another step's short
 * estimate, and Delete, Duplicate, Cancel and Save. A saved step's timing
 * is its column's: the column's header sets it, as a gate card's chip sets
 * its own. Nothing is saved until Save.
 */
export default function AutomationsTabEditor({
  target,
  onClose,
  onSaved,
  onDuplicate,
}: {
  target: EditorTarget;
  onClose: () => void;
  /** Saved or deleted; the automation to show (null when it was deleted). */
  onSaved: (automationId: string | null) => void;
  /** Duplicate: a copy of the step as it is here, as a new step after it. */
  onDuplicate: (automationId: string, step: Step) => void;
}) {
  const ref = useReference();
  const api = useAutomationsApi();
  const [draft, setDraft] = useState<AutomationDraft | null>(target.draft);
  const [stepId, setStepId] = useState<string | null>(target.stepId);
  const [problems, setProblems] = useState<string[]>([]);
  const [warn, setWarn] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // A new automation, ready to save on: its turn-on confirm shows first.
  const [turningOn, setTurningOn] = useState<AutomationDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [focusWords, setFocusWords] = useState(false);
  const initial = useRef(JSON.stringify(target.draft));
  // Only a new automation picks its trigger here.
  const showTrigger = useRef(target.mode === "new");
  const bodyRef = useRef<HTMLDivElement>(null);

  const step = draft?.steps.find((s) => s.id === stepId) ?? null;
  const gate =
    (target.gateId && draft?.gates?.find((g) => g.id === target.gateId)) ||
    null;
  const gateCard = gate?.cards[0] ?? null;
  const dirty = !!draft && JSON.stringify(draft) !== initial.current;

  useEffect(() => {
    if (!focusWords) return;
    setFocusWords(false);
    bodyRef.current
      ?.querySelector<HTMLInputElement>(WORDS_INPUT)
      ?.focus({ preventScroll: true });
  }, [focusWords]);

  const change = (next: Step) => {
    setProblems([]);
    setWarn(false);
    setDraft(
      (d) =>
        d && { ...d, steps: d.steps.map((s) => (s.id === next.id ? next : s)) }
    );
  };

  /** Add next step's When: the new step, at that timing, after the lane's last column. */
  const changeWhen = (position: Position) => {
    setProblems([]);
    setWarn(false);
    setDraft((d) => {
      const own = d?.steps.find((s) => s.id === stepId);
      return d && own
        ? {
            ...d,
            steps: withNext(
              d.steps,
              { ...own, position: clone(position) },
              d.gates ?? []
            ),
          }
        : d;
    });
  };

  /** The new gate, changed: its one card so far. */
  const changeGate = (next: (gate: Gate) => Gate) => {
    setProblems([]);
    setWarn(false);
    setDraft(
      (d) =>
        d && {
          ...d,
          gates: (d.gates ?? []).map((g) =>
            g.id === target.gateId ? next(g) : g
          ),
        }
    );
  };

  /** The gate's kind, picked from its grid: its card, at the kind's own timing. */
  const pickGateKind = (kind: string) => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    changeGate((g) => ({
      ...g,
      cards: [
        newCard(ref, triggerFor(ref, kind, tasksBefore(draft?.steps ?? [], g))),
      ],
    }));
  };

  const changeGateTrigger = (trigger: Trigger) =>
    changeGate((g) => ({
      ...g,
      cards: g.cards.map((card, i) =>
        i === 0
          ? {
              ...card,
              trigger,
              time:
                trigger.kind === card.trigger.kind
                  ? card.time
                  : cardTime(ref, trigger, null),
            }
          : card
      ),
    }));

  const changeGateTime = (time: TimePosition) =>
    changeGate((g) => ({
      ...g,
      cards: g.cards.map((card, i) => (i === 0 ? { ...card, time } : card)),
    }));

  const changeTrigger = (next: Trigger) => {
    setProblems([]);
    setWarn(false);
    setDraft(
      (d) =>
        d &&
        fitToTrigger(ref, {
          ...d,
          trigger: next,
          steps: d.steps.map((s) =>
            s.id === stepId ? followTrigger(ref, d.trigger, next, s) : s
          ),
        })
    );
  };

  const pick = (kind: string) => {
    const made = newDraft(ref, kind);
    // The fields start at the top, not where the list of kinds was scrolled to.
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    initial.current = JSON.stringify(made);
    showTrigger.current = true;
    setDraft(made);
    setStepId(made.steps[0].id);
    if (isTaskKind(kind)) setFocusWords(true);
  };

  const save = async () => {
    if (!draft || !step) return;
    const triggerChanged =
      target.mode === "new" ||
      JSON.stringify(target.draft?.trigger) !== JSON.stringify(draft.trigger);
    const found = [
      ...(gate ? gateProblems(ref, draft, gate) : []),
      ...stepProblems(ref, draft, step),
      ...(triggerChanged
        ? draft.steps
            .filter((s) => s.id !== step.id)
            .flatMap((s) =>
              stepProblems(ref, draft, s).map(
                (text) => `${plainTitle(ref, s)}: ${text}`
              )
            )
        : []),
    ];
    setProblems(found);
    if (found.length) return;
    const steps = orderSteps(draft.steps);
    if (target.mode === "new") {
      setTurningOn({
        name: suggestName(ref, { trigger: draft.trigger, steps }),
        on: true,
        trigger: draft.trigger,
        steps,
      });
      return;
    }
    setBusy(true);
    try {
      const id = target.automationId as string;
      const saved = await api.getAutomation(id);
      if (!saved) {
        Notifications.error(
          "That automation was deleted, so there was nothing to save."
        );
        onClose();
        return;
      }
      const next = { trigger: draft.trigger, steps };
      await api.updateAutomation(id, {
        ...next,
        ...(draft.gates ? { gates: draft.gates } : {}),
        name: nameAfter(ref, saved, next),
      });
      onSaved(id);
    } catch (e) {
      setProblems([errorText(e, "It could not be saved.")]);
    } finally {
      setBusy(false);
    }
  };

  const askDelete = () => {
    if (!draft || !step) return;
    if (waitersOf(draft.gates ?? [], step.id).length) {
      setProblems([
        "A trigger gate waits for this task. Change that gate first.",
      ]);
      return;
    }
    setDeleting(true);
  };

  const remove = async () => {
    if (!step) return;
    const id = target.automationId as string;
    setBusy(true);
    try {
      const saved = await api.getAutomation(id);
      if (!saved) {
        onClose();
        return;
      }
      const rest = saved.steps.filter((s) => s.id !== step.id);
      if (!rest.length) {
        await api.deleteAutomation(id);
        Notifications.success(`${saved.name} was deleted.`);
        onSaved(null);
        return;
      }
      const next = { trigger: saved.trigger, steps: orderSteps(rest) };
      await api.updateAutomation(id, {
        ...next,
        name: nameAfter(ref, saved, next),
      });
      onSaved(id);
    } catch (e) {
      // Back to this dialog, which says why.
      setDeleting(false);
      setProblems([errorText(e, "It could not be deleted.")]);
    } finally {
      setBusy(false);
    }
  };

  const onlyStep = target.mode === "edit" && target.draft?.steps.length === 1;
  const turnsOn = target.mode === "new";
  const estimate = useStepEstimate(target.automationId, draft, step);
  // Back to what opened it: the button, or a card's title.
  const returnFocus = target.anchor.matches("button, a")
    ? target.anchor
    : target.anchor.querySelector<HTMLElement>("[data-card-title]");

  // Add a trigger gate shows its kinds first, and nothing to save yet.
  const picking = target.mode === "gate" && !gateCard;
  const footer = draft && step && !picking && (
    <>
      {problems.length > 0 && (
        <ul className="mb-3 space-y-1 text-sm text-red-600" role="alert">
          {problems.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
      )}
      {warn && (
        <p
          className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"
          role="status"
        >
          Save or cancel your changes first.
        </p>
      )}
      <div className="flex items-center gap-2">
        {target.mode === "edit" && (
          <>
            <IconTip label="Delete">
              <Button
                color={ButtonColor.RED}
                fill={ButtonFill.HOLLOW}
                size={ButtonSize.SMALL}
                aria-label="Delete this step"
                disabled={busy}
                onClick={askDelete}
              >
                <TrashIcon />
              </Button>
            </IconTip>
            <IconTip label="Duplicate">
              <Button
                color={ButtonColor.PURPLE}
                fill={ButtonFill.HOLLOW}
                size={ButtonSize.SMALL}
                aria-label="Duplicate this step"
                disabled={busy}
                onClick={() => onDuplicate(target.automationId as string, step)}
              >
                <CopyIcon />
              </Button>
            </IconTip>
          </>
        )}
        <span className="flex-1" />
        <Button
          color={ButtonColor.SKYBLUE}
          fill={ButtonFill.HOLLOW}
          size={ButtonSize.SMALL}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          color={ButtonColor.GREEN}
          size={ButtonSize.SMALL}
          disabled={busy}
          onClick={save}
        >
          {turnsOn ? "Save and turn on" : "Save"}
        </Button>
      </div>
    </>
  );

  return (
    <EditorDialog
      title={target.title}
      fixed
      dirty={dirty}
      onClose={onClose}
      onWarn={() => setWarn(true)}
      returnFocus={returnFocus}
      bodyRef={bodyRef}
      footer={footer || undefined}
    >
      {!draft && <TriggerKindGrid onPick={pick} />}

      {picking && <TriggerKindGrid onPick={pickGateKind} />}

      {draft && step && !picking && (
        <>
          {showTrigger.current && (
            <TriggerPicker value={draft.trigger} onChange={changeTrigger} />
          )}

          {gate && gateCard && (
            <>
              <GateCardFields
                trigger={gateCard.trigger}
                tasks={tasksBefore(draft.steps, gate)}
                onChange={changeGateTrigger}
              />
              {gateCard.time && kindTiming(ref, gateCard.trigger.kind) && (
                <div className="mt-3">
                  <WhenField
                    words={cardTimeWords(ref, gateCard.trigger, gateCard.time)}
                    timing={kindTiming(ref, gateCard.trigger.kind)}
                    later={false}
                    position={gateCard.time}
                    onPick={changeGateTime}
                  />
                </div>
              )}
            </>
          )}

          {target.when && (
            <NextWhenField
              automation={{
                ...draft,
                steps: draft.steps.filter((s) => s.id !== step.id),
              }}
              position={step.position}
              onPick={changeWhen}
            />
          )}

          <FieldRow
            label="Then"
            className={
              showTrigger.current || gate ? "mt-4" : target.when ? "mt-3" : ""
            }
          >
            <ActionSelect
              value={step.action}
              trigger={draft.trigger}
              onChange={(action) =>
                change(switchAction(ref, draft, step, action))
              }
            />
          </FieldRow>

          <StepActionFields
            step={step}
            onChange={change}
            trigger={draft.trigger}
            steps={draft.steps}
            fill
          />

          <div className="mt-4">
            <AskFirstControl
              value={step.askFirst}
              onChange={(askFirst) => change({ ...step, askFirst })}
              trigger={draft.trigger}
              action={step.action}
            />
          </div>

          {step.action === "message" && (
            <StepMessagePreview
              key={step.message.templateId ?? "none"}
              automation={draft}
              saved={target.draft}
              step={step}
            />
          )}

          {estimate && <p className="mt-3 text-sm text-gray-600">{estimate}</p>}
        </>
      )}

      {turningOn && (
        <TurnOnDialog
          name={turningOn.name}
          target={{ draft: turningOn }}
          onTurnOn={(turnOn) =>
            api.createAutomation(turningOn, turnOn).then((made) => {
              Notifications.success(`${made.name} is saved and on.`);
              setTurningOn(null);
              onSaved(made.id);
            })
          }
          onClose={() => setTurningOn(null)}
        />
      )}

      <Dialog open={deleting} onOpenChange={setDeleting}>
        {deleting && (
          <DialogContent className="dialog-content">
            <h2 className="mb-2 text-xl font-semibold">
              {onlyStep
                ? `Delete ${draft?.name || "this automation"}?`
                : "Delete this step?"}
            </h2>
            {onlyStep && (
              <p className="text-sm text-gray-700">
                It is its only step. What it already did stays in Activity.
              </p>
            )}
            <DialogActions>
              <DialogAction
                primary={true}
                color={ButtonColor.RED}
                disabled={busy}
                onClick={remove}
              >
                Delete
              </DialogAction>
              <DialogClose asChild>
                <DialogAction color={ButtonColor.SKYBLUE}>Cancel</DialogAction>
              </DialogClose>
            </DialogActions>
          </DialogContent>
        )}
      </Dialog>
    </EditorDialog>
  );
}
